import {
  Bar,
  BarChart,
  Brush,
  CartesianGrid,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  AXIS_TICK,
  CHART,
  CHART_MARGIN,
  GRID_PROPS,
  TIME_SYNC_ID,
  Y_AXIS_WIDTH,
  formatDayLong,
  formatMonthTick,
  monthStartTicks,
} from '../../config/chartTheme';
import { firstFutureTs, type TimeRow } from './timeRows';

/**
 * Buchungs-Signale je Tag, unter dem Belegungsverlauf.
 *
 * Bewusst **nicht** als zweite Achse im Belegungsverlauf: Prozent und Anzahl
 * haben keine gemeinsame Skala, ihre Ausrichtung wäre willkürlich und würde
 * eine Korrelation suggerieren, die die Daten nicht hergeben. Geteilte
 * X-Achse über `syncId`, getrennte Y-Achse.
 */
export default function SignalTimeline({
  rows,
  height = 130,
  withBrush = false,
  onBrushChange,
}: {
  rows: TimeRow[];
  height?: number;
  /** Nur EIN Diagramm braucht die Zoom-Leiste; über `syncId` folgen die anderen. */
  withBrush?: boolean;
  onBrushChange?: (range: { startIndex: number; endIndex: number }) => void;
}) {
  const futureStart = firstFutureTs(rows);
  const ticks = monthStartTicks(rows.map(r => r.iso), rows.length > 400 ? 2 : 1);

  return (
    <ResponsiveContainer width="100%" height={height}>
      <BarChart
        data={rows}
        margin={CHART_MARGIN}
        syncId={TIME_SYNC_ID}
        // Der 10-%-Vorgabewert würde bei Tagesbalken unter 1 px Breite den
        // Balken vollständig auffressen.
        barCategoryGap={0}
      >
        <CartesianGrid {...GRID_PROPS} vertical={false} />
        {futureStart != null && (
          <ReferenceArea x1={futureStart} fill="#0b0b0b" fillOpacity={0.03} ifOverflow="hidden" />
        )}

        <XAxis
          dataKey="ts"
          type="number"
          scale="time"
          domain={['dataMin', 'dataMax']}
          ticks={ticks}
          tickFormatter={formatMonthTick}
          padding="gap"
          tick={AXIS_TICK}
          stroke={CHART.axis}
          tickLine={false}
        />
        <YAxis
          tick={AXIS_TICK}
          stroke={CHART.axis}
          tickLine={false}
          width={Y_AXIS_WIDTH}
          allowDecimals={false}
        />
        <Tooltip content={<SignalTooltip />} cursor={{ fill: '#0b0b0b', fillOpacity: 0.03 }} />

        {/* Zwei Serien statt eines <Cell> je Punkt: bei 730 Tagen wären das
            730 React-Elemente pro Render, nur um die Zukunft heller zu färben.
            Kein `radius` — bei ~1 px Balkenbreite wäre die Rundung breiter als
            der Balken. */}
        <Bar dataKey="signalsPast" name="Buchungs-Signale" fill={CHART.signal} isAnimationActive={false} />
        <Bar
          dataKey="signalsFuture"
          name="Signale (Zukunft)"
          fill={CHART.signal}
          fillOpacity={0.45}
          isAnimationActive={false}
        />

        {withBrush && (
          <Brush
            dataKey="ts"
            height={26}
            travellerWidth={8}
            stroke={CHART.axis}
            fill="#fcfcfb"
            tickFormatter={() => ''}
            // onDragEnd statt onChange: sonst würde die Tabelle bei jedem Pixel
            // der Ziehbewegung neu gerendert.
            onDragEnd={onBrushChange}
          />
        )}
      </BarChart>
    </ResponsiveContainer>
  );
}

function SignalTooltip({ active, payload }: { active?: boolean; payload?: { payload: TimeRow }[] }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  const signals = row.signalsPast ?? row.signalsFuture;
  if (signals == null) return null;
  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm px-3 py-2 text-xs">
      <div className="font-semibold text-gray-700">{formatDayLong(row.iso)}</div>
      <div className="mt-1 text-gray-700">
        {signals} {signals === 1 ? 'Signal' : 'Signale'}
      </div>
      <div className="mt-1 text-gray-400">Datenbasis: {row.n ?? 0} Inserate</div>
    </div>
  );
}
